// ============================================================================
// GESTIMMONY — Page Reset Password
// ============================================================================
// 2 modes selon la présence du token dans l'URL :
//   - Sans token (/forgot-password)   → demander l'envoi du lien
//   - Avec token (/reset-password/:token) → saisir le nouveau mot de passe
// En dev, l'endpoint /auth/password-reset/request retourne un `dev_link`
// affiché directement à l'écran pour tester sans configuration email.
// ============================================================================
import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { KeyRound, Mail, ArrowLeft } from "lucide-react";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [devLink, setDevLink] = useState(null);

  const requestReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password-reset/request", { email });
      toast.success("Si l'email existe, un lien vient d'être envoyé.");
      if (data.dev_link) setDevLink(data.dev_link);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
    setBusy(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: password });
      toast.success("Mot de passe mis à jour");
      navigate("/login");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <Card className="w-full max-w-md p-8" data-testid="reset-password-card">
        <Link to="/login" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-teal-700 mb-6" data-testid="reset-back-login">
          <ArrowLeft className="h-4 w-4" /> Retour à la connexion
        </Link>

        {!token ? (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center">
                <Mail className="h-5 w-5 text-teal-700" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl">Mot de passe oublié</h1>
                <p className="text-sm text-slate-500">Un lien vous sera envoyé par email</p>
              </div>
            </div>
            <form onSubmit={requestReset} className="space-y-4">
              <div>
                <Label htmlFor="email">Email de votre compte</Label>
                <Input id="email" data-testid="reset-email-input" type="email" required
                       value={email} onChange={(e) => setEmail(e.target.value)}
                       placeholder="vous@example.com" />
              </div>
              <Button type="submit" className="w-full" disabled={busy} data-testid="reset-request-submit">
                {busy ? "Envoi..." : "Envoyer le lien"}
              </Button>
            </form>
            {devLink && (
              <div className="mt-6 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs" data-testid="reset-dev-link-box">
                <p className="font-semibold text-amber-900 mb-1">🛠️ Mode dev — Lien direct :</p>
                <a href={devLink} className="text-teal-700 break-all hover:underline" data-testid="reset-dev-link">{devLink}</a>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-lg bg-teal-100 flex items-center justify-center">
                <KeyRound className="h-5 w-5 text-teal-700" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-xl">Nouveau mot de passe</h1>
                <p className="text-sm text-slate-500">Choisissez un mot de passe fort (6 caractères min.)</p>
              </div>
            </div>
            <form onSubmit={confirmReset} className="space-y-4">
              <div>
                <Label htmlFor="pwd">Nouveau mot de passe</Label>
                <Input id="pwd" data-testid="reset-new-pwd" type="password" required minLength={6}
                       value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="pwd2">Confirmer</Label>
                <Input id="pwd2" data-testid="reset-confirm-pwd" type="password" required minLength={6}
                       value={confirm} onChange={(e) => setConfirm(e.target.value)} />
              </div>
              <Button type="submit" className="w-full" disabled={busy} data-testid="reset-confirm-submit">
                {busy ? "Enregistrement..." : "Mettre à jour"}
              </Button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
